import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { KeyValuePair, SettingsFactory, UserSettingsDto } from '@lfw/roesberg.liveforms.core';
import { EditorSettings, SupportedLanguages } from '@lfw/roesberg.liveforms.editor';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfig } from '../../appconfig';
import { AppUrls } from '../../route/app-urls';
import { Settings } from './settings';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  public settings!: Settings;
  subscription: Subscription = new Subscription();

  public settingsLoaded: Subject<boolean> = new Subject<boolean>();

  private readonly http = inject(HttpClient);
  private readonly config = inject(AppConfig);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  constructor() {
    this.settings = new Settings(this.config, this.translate);
    this.subscription.add(this.settings.settingsChanged.subscribe((val) => this.settingsChanged(val)));
    this.subscription.add(
      this.router.events.subscribe(() => {
        // reset fullscreen mode when route changes
        this.settings.viewMode.isFullScreen = false;
      })
    );
  }

  public getSettings(): Observable<boolean> {
    return this.http.get<UserSettingsDto>(AppUrls.getApiUrl(AppUrls.urlSettings, 'get')).pipe(
      map((dtoRaw) => {
        this.settings.settingsList = SettingsFactory.fromDTO(dtoRaw.items);
        this.settings.parseAndApplySettingsList();
        this.settingsLoaded.next(true);
        return true;
      }),
      catchError(() => {
        this.settings.settingsList = [];
        return of(false);
      })
    );
  }

  private settingsChanged(val: KeyValuePair) {
    if (val == null) {
      return;
    }
    const arr = [];
    arr.push(val);
    this.http
      .post<KeyValuePair>(AppUrls.getApiUrl(AppUrls.urlSettings, 'save'), JSON.stringify(arr))
      .pipe()
      .subscribe();
  }

  public IsFullScreen() {
    return this.settings.viewMode.isFullScreen || this.IsRunningInWindowModeAndOpenInNewTabMode();
  }

  public IsRunningInWindowModeAndOpenInNewTabMode() {
    return this.RunningInWindowMode() && this.settings.orderSettings.openOrderInNewTab;
  }

  public RunningInWindowMode() {
    return navigator.userAgent.indexOf('LFW-Window-Android') != -1;
  }

  public getEditorSettings(): EditorSettings {
    const settings = new EditorSettings(
      (this.translate.getCurrentLang() ?? this.translate.getFallbackLang()) as SupportedLanguages
    );

    const configuredMaxSizeMb = this.config.maxImageUploadSize();
    settings.maxImageUploadSize = configuredMaxSizeMb;
    return settings;
  }
}
